import type { Pet } from '.';

import { action } from '@play-co/replicant';
import PetSpecies from 'src/replicant/airtable/PetSpecies';
import PetNames from 'src/replicant/airtable/PetNames';
import { getRandomItemFromArray } from 'src/replicant/utils/random';
import { defineActions } from '../../utils';

export const petActions = (
  Pet: Pick<Pet, 'ruleset' | 'schema' | 'getters' | 'modifiers'>,
  { PetStats, Level, Encyclopedia, Fate, History }: Pet['$DEPENDENCIES'],
) => {
  return defineActions<Pet>()({
    /**
     * Create new pet on first game session
     */
    createNewPet: action((state, _: void, api) => {
      const babyPets = PetSpecies.filter(
        (pet) => pet.stage === 'baby',
      ) as Extract<typeof PetSpecies[number], { stage: 'baby' }>[];
      const newPet = babyPets[Math.floor(babyPets.length * api.math.random())];

      // Assign pet name
      const { pet, settings } = state;
      const localeSuffix = settings.locale === 'ja' ? 'jp' : 'en';
      pet.name = getRandomItemFromArray(PetNames, api.math.random)[
        `name_${localeSuffix}`
      ];

      const { addEvolution } = Pet.modifiers(state);
      const { setAttribute } = PetStats.modifiers;
      const { resolveStatusEffect } = Fate.api;

      // Add first evolution
      const now = api.date.now();
      addEvolution(newPet.id, newPet.stage, now);

      // Set starting attributes
      setAttribute(state, 'love', newPet.love_ini, now);
      setAttribute(state, 'dirtiness', newPet.dirtiness_ini, now);
      setAttribute(state, 'stomach', newPet.stomach_ini, now);
      setAttribute(state, 'toothAcidity', newPet.tooth_acidity_ini, now);
      setAttribute(state, 'urine', newPet.urine_ini, now);

      // Clear all status effects
      resolveStatusEffect(state, 'asleep', false, now);
      resolveStatusEffect(state, 'evolving', false, now);
      resolveStatusEffect(state, 'scared', false, now);
      resolveStatusEffect(state, 'sick', false, now);
      resolveStatusEffect(state, 'toothDecay', false, now);
      resolveStatusEffect(state, 'dying', false, now);
      resolveStatusEffect(state, 'dead', false, now);
      resolveStatusEffect(state, 'hadVisitor', false, now);
      resolveStatusEffect(state, 'wantToGoOut', false, now);

      // Determine fate
      Fate.api.update(state, now);

      Encyclopedia.modifiers(state).setSpeciesState(newPet.id, 'unlocked');
    }),

    /**
     * Reborn pet after death
     */
    rebornPet: action((state, _: void, api) => {
      const { getNextRebornSpecies } = Pet.getters(state);

      // Get next reborn species
      const newPet = getNextRebornSpecies(api.math.random);

      // Spawn new pet
      Pet.modifiers(state).spawnPet(
        'death',
        newPet,
        api.date.now(),
        api.math.random,
      );
    }),

    /**
     * Update name
     */
    updateName: action((state, name: string, api) => {
      if (name.length < 1)
        throw new Error('Pet name name must be more than 1 character');
      if (name.length > 8)
        throw new Error('Pet name must be less than 8 characters');

      state.pet.name = name;
    }),

    /**
     * Pet evolution action
     */
    evolvePet: action((state, _: void, api) => {
      const {
        getCurrentEvolution,
        getAnalyticsUserProperties,
        getAnalyticsAttributeUserProperties,
        hasStatusEffect,
      } = Pet.getters(state);
      const currentEvolution = getCurrentEvolution();
      const nextEvolution = Pet.getters(state).getNextSpeciesEvolution(
        api.math.random,
      );
      if (!nextEvolution) {
        throw new Error(
          `Invalid evolution attempt from species ${currentEvolution.species}`,
        );
      }

      // Do not evolve if there is no evolving status effect
      const now = api.date.now();
      if (!hasStatusEffect('evolving', now)) {
        throw new Error('No evolving status effect');
      }

      // Save current attributes value to state before change species
      // If we won't do that species attributes interpolation will be wrong
      Pet.modifiers(state).syncAttributes(state, now);

      const { id, stage } = nextEvolution;
      Pet.modifiers(state).addEvolution(id, stage, now);
      Fate.api.resolveStatusEffect(state, 'evolving', false, now);

      // Determine new fate
      Fate.api.update(state, now);

      const timeFromLastEvolution = now - currentEvolution.timestamp;
      api.sendAnalyticsEvents([
        {
          eventType: 'PetEvolution',
          eventProperties: {
            old_generation: currentEvolution.stage,
            new_generation: stage,
            old_pet_id: currentEvolution.species,
            new_pet_id: id,
            sec_from_last_evolution: Math.floor(timeFromLastEvolution / 1000),
          },
          userProperties: {
            ...getAnalyticsUserProperties(now, false),
            ...getAnalyticsAttributeUserProperties(now),
          },
        },
      ]);

      // Grant xp.
      const analyticsEvents = Level.modifiers(state).incrementXp(
        'evolution',
        stage,
      );
      api.sendAnalyticsEvents(analyticsEvents);

      Encyclopedia.modifiers(state).setSpeciesState(id, 'unlocked');
    }),
  });
};
